/**
 * Helpers of the History panel and the Versions dialog (W2-10 projects-data; DESIGN.md §4.18, §4.21, §5.13;
 * ARCHITECTURE.md §10).
 *
 *  - history entry icons by command namespace, relative times, sizes;
 *  - "jump to the change": undo/redo carry a focus (beat, line); the panel's jumps reveal the last one;
 *  - version kinds and their names, newest first;
 *  - **restore = one undoable transaction**: the current state is versioned first ('beforeRestore'), then
 *    `tx.applyOps(Ops.diff(tx.doc, versionDoc))` turns the document into the version in one step (kind 'restore');
 *    the selection is cleared (ids may not exist in the version) and the active line kept when it still exists.
 */

import type { AppServices, IconName, KeymapService } from '../../app/registry/types';
import type { ChartDoc, LineId, ProjectId } from '../../core/model/types';
import { Ops } from '../../core/ops/ops';
import type { SelectionSnapshot, Txn, TxBuilder, TxnFocus } from '../../core/ops/types';
import { Beats } from '../../core/time/beat';
import type { MessageRef } from '../../core/util/types';
import type { MessageKey } from '../../i18n/types';
import type { VersionInfo, VersionKind } from '../../persist/types';
import type { DocStore, HistoryEntry, HistoryView } from '../../state/types';
import { m } from './history.messages';

// ---------------------------------------------------------------------------------------------------------------
// Entries
// ---------------------------------------------------------------------------------------------------------------

/** Icon of a history entry by the namespace of the command that made it. */
const NAMESPACE_ICONS: ReadonlyArray<readonly [prefix: string, icon: IconName]> = [
  ['notes.', 'CircleDot'],
  ['edit.paste', 'ClipboardPaste'],
  ['edit.', 'Scissors'],
  ['tools.', 'Wand'],
  ['transform.', 'Move'],
  ['keys.', 'Spline'],
  ['segments.', 'Spline'],
  ['curve.', 'Spline'],
  ['motion.', 'Spline'],
  ['easing.', 'Spline'],
  ['templates.', 'Shapes'],
  ['layers.', 'Layers'],
  ['lines.', 'Rows3'],
  ['timing.', 'Timer'],
  ['markers.', 'Flag'],
  ['time.', 'MoveVertical'],
  ['lane.', 'Rows3'],
  ['inspector.', 'SlidersHorizontal'],
  ['assets.', 'Files'],
  ['project.', 'FileText'],
  ['file.', 'FolderInput'],
  ['raw.', 'Braces'],
  ['problems.', 'TriangleAlert'],
  ['select.', 'SquareDashedMousePointer'],
  ['effects.', 'Sparkles'],
];

export function entryIcon(command: string | undefined): IconName {
  if (command === undefined) return 'Pencil';
  for (const [prefix, icon] of NAMESPACE_ICONS) if (command.startsWith(prefix)) return icon;
  return 'Pencil';
}

/** History facts of the contract's HistoryView the panel shows (CR-W1-09-2). */
export interface HistoryExtras {
  /** Older changes are not available for undo ("Earlier changes: see Versions"). */
  readonly truncated: boolean;
  /** Entries dropped by the memory cap since the history was created, loaded or reset. */
  readonly droppedEntries: number;
}

export function historyExtras(h: HistoryView): HistoryExtras {
  return { truncated: h.truncated, droppedEntries: h.droppedEntries };
}

/** Entries without a visible effect (a coalesced run that came back to where it started). */
export function noEffect(e: HistoryEntry): boolean {
  return e.noEffect === true;
}

/**
 * Goes to a history position (History panel click) and returns the focus of the last step taken, for "jump to the
 * change". Later entries stay redo-able until the next edit.
 */
export function jumpTo(store: DocStore, position: number): TxnFocus | null {
  let last: Txn | null = null;
  const off = store.subscribeCommits((txn) => {
    last = txn;
  });
  try {
    store.jumpTo(position);
  } finally {
    off();
  }
  return (last as Txn | null)?.focus ?? null;
}

/** Reveals a change: makes its line active and scrolls the timeline to its beat (when one is mounted). */
export function revealFocus(services: Pick<AppServices, 'editor' | 'timeline' | 'store'>, focus: TxnFocus): void {
  const store = services.store;
  if (focus.line !== undefined && store?.doc.lines.has(focus.line) === true) {
    services.editor.getState().setActiveLine(focus.line);
  }
  if (focus.beat !== undefined) {
    try {
      services.timeline?.reveal(Beats.toF64(focus.beat), 'center');
    } catch {
      // The timeline may be unmounted (another workspace).
    }
  }
}

/**
 * The shortcuts of a command in the user's keymap, as the platform shows them ("Ctrl+Z or Ctrl+Y", "Cmd+S"); null
 * when the command has none.
 */
export function shortcutText(
  keymap: Pick<KeymapService, 'bindingsFor' | 'label'>,
  locale: string,
  command: string,
): string | null {
  const chords = keymap.bindingsFor(command).map((c) => keymap.label(c));
  if (chords.length === 0) return null;
  try {
    return new Intl.ListFormat(locale, { style: 'long', type: 'disjunction' }).format(chords);
  } catch {
    return chords.join(', ');
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Sizes and times
// ---------------------------------------------------------------------------------------------------------------

const SIZE_UNITS: readonly MessageKey[] = [m.sizeB, m.sizeKB, m.sizeMB, m.sizeGB, m.sizeTB];

export interface SizeText {
  readonly locale: string;
  readonly t: (key: MessageKey, params?: Readonly<Record<string, string | number>>) => string;
}

/** "0 B", "12 KB", "3.4 MB" (1024-based, locale digits, translated unit). */
export function formatBytes(bytes: number, text: SizeText): string {
  let v = Number.isFinite(bytes) && bytes > 0 ? bytes : 0;
  let unit = 0;
  while (v >= 1024 && unit < SIZE_UNITS.length - 1) {
    v /= 1024;
    unit++;
  }
  const digits = unit === 0 ? 0 : v < 10 ? 1 : 0;
  const size = new Intl.NumberFormat(text.locale, { maximumFractionDigits: digits }).format(v);
  return text.t(SIZE_UNITS[unit] ?? m.sizeB, { size });
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** "3 minutes ago", "yesterday" (Intl.RelativeTimeFormat); null below one minute ("just now"). */
export function relativeTime(at: number, now: number, locale: string): string | null {
  const diff = at - now;
  const abs = Math.abs(diff);
  if (abs < 60_000) return null;
  const units: ReadonlyArray<readonly [Intl.RelativeTimeFormatUnit, number]> = [
    ['minute', 60_000],
    ['hour', 3_600_000],
    ['day', DAY_MS],
    ['week', 7 * DAY_MS],
    ['month', 30 * DAY_MS],
    ['year', 365 * DAY_MS],
  ];
  let chosen: readonly [Intl.RelativeTimeFormatUnit, number] = ['minute', 60_000];
  for (const u of units) if (abs >= u[1]) chosen = u;
  return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(Math.round(diff / chosen[1]), chosen[0]);
}

/** "5 Oct 2026, 14:03". */
export function formatDateTime(at: number, locale: string): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(at);
}

// ---------------------------------------------------------------------------------------------------------------
// Versions
// ---------------------------------------------------------------------------------------------------------------

export const VERSION_KIND_TITLES: Readonly<Record<VersionKind, MessageKey>> = {
  auto: m.kindAuto,
  checkpoint: m.kindCheckpoint,
  import: m.kindImport,
  export: m.kindExport,
  beforeRestore: m.kindBeforeRestore,
  beforeGenerator: m.kindBeforeGenerator,
};

export const VERSION_KIND_ICONS: Readonly<Record<VersionKind, IconName>> = {
  auto: 'Clock',
  checkpoint: 'Bookmark',
  import: 'FolderInput',
  export: 'PackageCheck',
  beforeRestore: 'RotateCcw',
  beforeGenerator: 'Wand',
};

/** Checkpoints and exports are always kept; automatic versions are thinned under the budget. */
export function isKept(kind: VersionKind): boolean {
  return kind === 'checkpoint' || kind === 'export';
}

/**
 * Versions newest first (same time: higher seq first). Versions at the same seq hold the same document and are one
 * record in storage (a later checkpoint there renames it); should a listing still carry several, the newest wins.
 */
export function sortVersions(versions: readonly VersionInfo[]): VersionInfo[] {
  const bySeq = new Map<number, VersionInfo>();
  for (const v of versions) {
    const prev = bySeq.get(v.seq);
    if (prev === undefined || v.at >= prev.at) bySeq.set(v.seq, v);
  }
  return [...bySeq.values()].sort((a, b) => b.at - a.at || b.seq - a.seq);
}

/** Selection after a restore: nothing selected; the active line kept when the version has it. */
function restoredSelection(doc: ChartDoc, activeLine: LineId | null): SelectionSnapshot {
  return {
    notes: [],
    keys: [],
    segments: [],
    lines: [],
    bpm: [],
    markers: [],
    effects: [],
    videos: [],
    groups: [],
    activeLine: activeLine !== null && doc.lines.has(activeLine) ? activeLine : (doc.lineOrder[0] ?? null),
  };
}

export type RestoreOutcome =
  { readonly ok: true; readonly txn: Txn | null } | { readonly ok: false; readonly reason: MessageRef };

/**
 * Restores version `seq` of the project open in `store` as ONE undoable transaction, after versioning the current
 * state ('beforeRestore'). `label` names the version in the history entry.
 */
export async function restoreVersion(
  services: Pick<AppServices, 'persist' | 'editor'>,
  store: DocStore,
  projectId: ProjectId,
  seq: number,
  label: string,
): Promise<RestoreOutcome> {
  if (store.projectId !== projectId) return { ok: false, reason: { key: m.restoreNotOpen } };
  if (store.gestureActive) return { ok: false, reason: { key: m.restoreGesture } };
  await services.persist.flush();
  await services.persist.checkpoint(projectId, null, 'beforeRestore');
  const target = await services.persist.loadVersion(projectId, seq);
  const meta = { label: { key: m.restoreTxn, params: { label } }, command: 'project.restoreVersion' };
  const activeLine = services.editor.getState().activeLine;
  const build = (tx: TxBuilder): void => {
    tx.applyOps(Ops.diff(tx.doc, target));
    tx.setSelection(restoredSelection(target, activeLine));
  };
  const out = store.transact({ ...meta, kind: 'restore' }, build);
  if (!out.ok) return { ok: false, reason: out.reason };
  return { ok: true, txn: out.txn };
}
